import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { BarChart3, Download } from 'lucide-react';
import { useRef, useEffect } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import {
  Box,
  AppLayout,
  AppCard,
  AppSkeleton,
  AppSelect,
  AppNudge,
  UpgradePrompt,
} from '@/components';
import { adminReportsApi, adminProvidersApi, adminBusinessApi } from '@/api';
import { useEntitlements } from '@/auth/useEntitlements';
import { useBusinessStore } from '@/stores';
import { useContextualNudge } from '@/hooks/useContextualNudge';
import { colors, radius } from '@/theme/tokens';
import type {
  GetNoShowReportResponse,
  GetRetentionReportResponse,
  ReportGranularity,
} from '@tilly/shared';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type DatePreset = '30d' | '90d' | '12mo' | 'custom';

interface Filters {
  preset: DatePreset;
  from: string;
  to: string;
  providerId: string;
  serviceId: string;
  granularity: ReportGranularity;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysAgo(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

function defaultFilters(): Filters {
  return {
    preset: '90d',
    from: daysAgo(90),
    to: today(),
    providerId: '',
    serviceId: '',
    granularity: 'week',
  };
}

function fmt(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

function downloadCsv(report: GetNoShowReportResponse) {
  const lines: string[] = [];
  // Summary
  lines.push('Section,Metric,Value');
  lines.push(`Summary,Total Booked,${report.summary.totalBooked}`);
  lines.push(`Summary,No-shows,${report.summary.noShowCount}`);
  lines.push(`Summary,Cancellations,${report.summary.cancelledCount}`);
  lines.push(`Summary,Completed,${report.summary.completedCount}`);
  lines.push(`Summary,No-show Rate,${fmt(report.summary.noShowRate)}`);
  lines.push(`Summary,Cancellation Rate,${fmt(report.summary.cancellationRate)}`);
  lines.push('');
  // Trend
  lines.push('Period,Booked,No-shows,Cancelled,No-show Rate,Cancellation Rate');
  for (const row of report.trend) {
    lines.push(
      `${row.bucketStart},${row.booked},${row.noShow},${row.cancelled},${fmt(row.noShowRate)},${fmt(row.cancellationRate)}`
    );
  }
  lines.push('');
  // Top clients
  lines.push('Client,No-shows,Total Booked,No-show Rate');
  for (const row of report.topClients) {
    lines.push(`${row.name},${row.noShowCount},${row.totalBooked},${fmt(row.noShowRate)}`);
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `tilly-no-show-report-${report.filters.from}-${report.filters.to}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function KpiCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <AppCard sx={{ p: 3, flex: 1, minWidth: 0 }}>
      <Box
        sx={{
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          color: colors.textMuted,
          mb: 1,
        }}
      >
        {label}
      </Box>
      <Box sx={{ fontSize: 28, fontWeight: 700, color: colors.textPrimary, lineHeight: 1.1 }}>
        {value}
      </Box>
      {sub && <Box sx={{ fontSize: 13, color: colors.textSecondary, mt: 0.5 }}>{sub}</Box>}
    </AppCard>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        fontSize: 15,
        fontWeight: 600,
        color: colors.textPrimary,
        mb: 2,
      }}
    >
      {children}
    </Box>
  );
}

interface BreakdownTableProps {
  headers: string[];
  rows: Array<{ id: string; cells: React.ReactNode[] }>;
  onRowClick?: (id: string) => void;
}

function BreakdownTable({ headers, rows, onRowClick }: BreakdownTableProps) {
  const { t } = useTranslation(['admin', 'common']);
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr>
            {headers.map((h) => (
              <th
                key={h}
                style={{
                  textAlign: 'left',
                  padding: '8px 12px',
                  borderBottom: `1px solid ${colors.border}`,
                  color: colors.textMuted,
                  fontWeight: 600,
                  fontSize: 12,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={row.id}
              onClick={() => onRowClick?.(row.id)}
              style={{
                backgroundColor: i % 2 === 0 ? 'transparent' : colors.surfaceRaised,
                cursor: onRowClick ? 'pointer' : undefined,
              }}
            >
              {row.cells.map((cell, j) => (
                <td
                  key={j}
                  style={{
                    padding: '10px 12px',
                    color: colors.textPrimary,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td
                colSpan={headers.length}
                style={{ padding: '24px 12px', color: colors.textMuted, textAlign: 'center' }}
              >
                {t('admin:reports.noData')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function ReportsPage() {
  const { t } = useTranslation(['admin', 'common']);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeBusinessId: businessId } = useBusinessStore();
  const { hasFeature } = useEntitlements();
  const hasReporting = hasFeature('reporting');

  const activeTab = (searchParams.get('tab') ?? 'no-show') as 'no-show' | 'retention';

  function switchTab(tab: 'no-show' | 'retention') {
    setSearchParams({ tab });
  }

  const [filters, setFilters] = useState<Filters>(defaultFilters);

  // Phase 6 nudge - Export CSV
  const exportButtonRef = useRef<HTMLButtonElement>(null);
  const [nudgeOpen, setNudgeOpen] = useState(false);
  const {
    shouldShow: showExportCsvNudge,
    recordImpression: recordExportCsv,
    nudgeProps: exportCsvProps,
  } = useContextualNudge('reports_export_csv', activeTab === 'no-show');

  useEffect(() => {
    if (showExportCsvNudge && !nudgeOpen) {
      const timer = setTimeout(() => setNudgeOpen(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [showExportCsvNudge, nudgeOpen]);

  // Providers and services for filter dropdowns
  const { data: providersData } = useQuery({
    queryKey: ['providers', businessId],
    queryFn: () => adminProvidersApi.getProviders(businessId!),
    enabled: !!businessId,
  });
  const { data: servicesData } = useQuery({
    queryKey: ['services', businessId],
    queryFn: () => adminBusinessApi.getServices(businessId!),
    enabled: !!businessId,
  });

  const {
    data: report,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [
      'no-show-report',
      businessId,
      filters.from,
      filters.to,
      filters.providerId,
      filters.serviceId,
      filters.granularity,
    ],
    queryFn: () =>
      adminReportsApi.getNoShowReport(businessId!, {
        from: filters.from,
        to: filters.to,
        providerId: filters.providerId || undefined,
        serviceId: filters.serviceId || undefined,
        granularity: filters.granularity,
      }),
    enabled: !!businessId && activeTab === 'no-show' && hasReporting,
  });

  const leadTimeChartData = useMemo(() => {
    const labelMap: Record<string, string> = {
      '<2h': t('admin:reports.leadTime.lt2h'),
      '2-24h': t('admin:reports.leadTime.h2to24'),
      '1-3d': t('admin:reports.leadTime.d1to3'),
      '>3d': t('admin:reports.leadTime.gt3d'),
    };
    return (report?.leadTime ?? []).map((b) => ({
      name: labelMap[b.bucket] ?? b.bucket,
      count: b.count,
    }));
  }, [report, t]);

  const trendChartData = useMemo(
    () =>
      (report?.trend ?? []).map((p) => ({
        date: p.bucketStart,
        noShowPct: +(p.noShowRate * 100).toFixed(1),
        cancelPct: +(p.cancellationRate * 100).toFixed(1),
        booked: p.booked,
      })),
    [report]
  );

  function setPreset(preset: DatePreset) {
    const to = today();
    let from: string;
    let granularity: ReportGranularity = 'week';
    switch (preset) {
      case '30d':
        from = daysAgo(30);
        break;
      case '12mo':
        from = daysAgo(365);
        granularity = 'month';
        break;
      default:
        from = daysAgo(90);
    }
    setFilters((f) => ({ ...f, preset, from, to, granularity }));
  }

  if (!businessId) return null;

  const tabButtonStyle = (isActive: boolean) => ({
    padding: '8px 20px',
    borderRadius: radius.sm,
    border: `1px solid ${isActive ? colors.accent : colors.border}`,
    background: isActive ? colors.accentLight : colors.surface,
    color: isActive ? colors.accentHover : colors.textSecondary,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  });

  return (
    <AppLayout>
      <Box sx={{ maxWidth: 1080 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mb: 3,
          }}
        >
          <BarChart3 size={22} color={colors.accent} />
          <Box sx={{ fontSize: 22, fontWeight: 700, color: colors.textPrimary }}>
            {t('admin:reports.title')}
          </Box>
        </Box>

        {/* Tab switcher */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
          <button
            style={tabButtonStyle(activeTab === 'no-show')}
            onClick={() => switchTab('no-show')}
          >
            {t('admin:reports.tabs.noShow')}
          </button>
          <button
            style={tabButtonStyle(activeTab === 'retention')}
            onClick={() => switchTab('retention')}
          >
            {t('admin:reports.tabs.retention')}
          </button>
        </Box>

        {!hasReporting && <UpgradePrompt feature="reporting" requiredTier="solo" />}

        {hasReporting && activeTab === 'no-show' && (
          <Box>
            {/* Sub-header row with export */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 3,
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ fontSize: 16, fontWeight: 600, color: colors.textPrimary }}>
                {t('admin:reports.noShow')}
              </Box>
              {report && (
                <button
                  ref={exportButtonRef}
                  onClick={() => downloadCsv(report)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '8px 14px',
                    borderRadius: radius.sm,
                    border: `1px solid ${colors.border}`,
                    background: colors.surface,
                    color: colors.textSecondary,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  <Download size={15} />
                  {t('admin:reports.exportCsv')}
                </button>
              )}
            </Box>

            {/* Filter bar */}
            <AppCard sx={{ p: 2, mb: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                }}
              >
                {/* Date preset buttons */}
                {(['30d', '90d', '12mo'] as DatePreset[]).map((p) => (
                  <button
                    key={p}
                    onClick={() => setPreset(p)}
                    style={{
                      padding: '6px 14px',
                      borderRadius: radius.sm,
                      border: `1px solid ${filters.preset === p ? colors.accent : colors.border}`,
                      background: filters.preset === p ? colors.accentLight : colors.surface,
                      color: filters.preset === p ? colors.accentHover : colors.textSecondary,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {t(
                      `admin:reports.filters.last${p === '30d' ? '30' : p === '90d' ? '90' : '12mo'}`
                    )}
                  </button>
                ))}

                {/* Provider filter */}
                <AppSelect
                  size="small"
                  value={filters.providerId}
                  onChange={(e) =>
                    setFilters((f) => ({
                      ...f,
                      providerId: e.target.value as string,
                      preset: 'custom',
                    }))
                  }
                  displayEmpty
                  sx={{ minWidth: 160, fontSize: 13 }}
                  options={[
                    { value: '', label: t('admin:reports.filters.provider') },
                    ...(providersData?.providers ?? []).map((p) => ({
                      value: p.userId,
                      label: p.name,
                    })),
                  ]}
                />

                {/* Service filter */}
                <AppSelect
                  size="small"
                  value={filters.serviceId}
                  onChange={(e) =>
                    setFilters((f) => ({
                      ...f,
                      serviceId: e.target.value as string,
                      preset: 'custom',
                    }))
                  }
                  displayEmpty
                  sx={{ minWidth: 160, fontSize: 13 }}
                  options={[
                    { value: '', label: t('admin:reports.filters.service') },
                    ...(servicesData?.services ?? []).map((s) => ({
                      value: s.id,
                      label: s.name,
                    })),
                  ]}
                />

                {/* Granularity toggle */}
                <AppSelect
                  size="small"
                  value={filters.granularity}
                  onChange={(e) =>
                    setFilters((f) => ({
                      ...f,
                      granularity: e.target.value as ReportGranularity,
                    }))
                  }
                  sx={{ minWidth: 100, fontSize: 13 }}
                  options={[
                    { value: 'week', label: t('admin:reports.filters.week') },
                    { value: 'month', label: t('admin:reports.filters.month') },
                  ]}
                />
              </Box>
            </AppCard>

            {isLoading && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <AppSkeleton height={120} />
                <AppSkeleton height={260} />
              </Box>
            )}

            {isError && (
              <Box sx={{ color: colors.error, py: 4, textAlign: 'center' }}>
                {t('admin:reports.error')}
              </Box>
            )}

            {report && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* KPI Cards */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <KpiCard
                    label={t('admin:reports.summary.totalBooked')}
                    value={String(report.summary.totalBooked)}
                  />
                  <KpiCard
                    label={t('admin:reports.summary.noShowRate')}
                    value={fmt(report.summary.noShowRate)}
                    sub={`${report.summary.noShowCount} ${t('admin:reports.summary.noShows').toLowerCase()}`}
                  />
                  <KpiCard
                    label={t('admin:reports.summary.cancellationRate')}
                    value={fmt(report.summary.cancellationRate)}
                    sub={`${report.summary.cancelledCount} ${t('admin:reports.summary.cancellations').toLowerCase()}`}
                  />
                  <KpiCard
                    label={t('admin:reports.summary.completed')}
                    value={String(report.summary.completedCount)}
                  />
                </Box>

                {/* Trend Chart */}
                <AppCard sx={{ p: 3 }}>
                  <SectionTitle>{t('admin:reports.trend.title')}</SectionTitle>
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart
                      data={trendChartData}
                      margin={{ top: 4, right: 16, left: -16, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11, fill: colors.textMuted }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        unit="%"
                        tick={{ fontSize: 11, fill: colors.textMuted }}
                        tickLine={false}
                        axisLine={false}
                        domain={[0, 'auto']}
                      />
                      <Tooltip
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        formatter={(value: any, name: any) => [
                          `${value ?? 0}%`,
                          name === 'noShowPct'
                            ? t('admin:reports.trend.noShowRate')
                            : t('admin:reports.trend.cancellationRate'),
                        ]}
                        contentStyle={{
                          borderRadius: radius.sm,
                          border: `1px solid ${colors.border}`,
                          fontSize: 12,
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="noShowPct"
                        stroke={colors.danger}
                        strokeWidth={2}
                        dot={false}
                        name="noShowPct"
                      />
                      <Line
                        type="monotone"
                        dataKey="cancelPct"
                        stroke={colors.warning}
                        strokeWidth={2}
                        dot={false}
                        name="cancelPct"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </AppCard>

                {/* Lead-time Distribution + By Provider side by side */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 3,
                  }}
                >
                  {/* Lead-time */}
                  <AppCard sx={{ p: 3 }}>
                    <SectionTitle>{t('admin:reports.leadTime.title')}</SectionTitle>
                    <Box sx={{ fontSize: 12, color: colors.textMuted, mb: 2 }}>
                      {t('admin:reports.leadTime.subtitle')}
                    </Box>
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart
                        data={leadTimeChartData}
                        margin={{ top: 4, right: 8, left: -20, bottom: 0 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke={colors.border}
                          vertical={false}
                        />
                        <XAxis
                          dataKey="name"
                          tick={{ fontSize: 11, fill: colors.textMuted }}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis
                          tick={{ fontSize: 11, fill: colors.textMuted }}
                          tickLine={false}
                          axisLine={false}
                          allowDecimals={false}
                        />
                        <Tooltip
                          contentStyle={{
                            borderRadius: radius.sm,
                            border: `1px solid ${colors.border}`,
                            fontSize: 12,
                          }}
                        />
                        <Bar dataKey="count" fill={colors.accent} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </AppCard>

                  {/* By Provider */}
                  <AppCard sx={{ p: 3 }}>
                    <SectionTitle>{t('admin:reports.byProvider.title')}</SectionTitle>
                    <BreakdownTable
                      headers={[
                        t('admin:reports.byProvider.provider'),
                        t('admin:reports.byProvider.booked'),
                        t('admin:reports.byProvider.noShowRate'),
                        t('admin:reports.byProvider.cancellationRate'),
                      ]}
                      rows={(report.byProvider ?? []).map((row) => ({
                        id: row.id,
                        cells: [
                          row.name,
                          row.booked,
                          fmt(row.noShowRate),
                          fmt(row.cancellationRate),
                        ],
                      }))}
                    />
                  </AppCard>
                </Box>

                {/* By Service */}
                <AppCard sx={{ p: 3 }}>
                  <SectionTitle>{t('admin:reports.byService.title')}</SectionTitle>
                  <BreakdownTable
                    headers={[
                      t('admin:reports.byService.service'),
                      t('admin:reports.byService.booked'),
                      t('admin:reports.byService.noShows'),
                      t('admin:reports.byService.cancellations'),
                      t('admin:reports.byService.noShowRate'),
                      t('admin:reports.byService.cancellationRate'),
                    ]}
                    rows={(report.byService ?? []).map((row) => ({
                      id: row.id,
                      cells: [
                        row.name,
                        row.booked,
                        row.noShow,
                        row.cancelled,
                        fmt(row.noShowRate),
                        fmt(row.cancellationRate),
                      ],
                    }))}
                  />
                </AppCard>

                {/* Top Clients Leaderboard */}
                <AppCard sx={{ p: 3 }}>
                  <SectionTitle>{t('admin:reports.topClients.title')}</SectionTitle>
                  <Box sx={{ fontSize: 12, color: colors.textMuted, mb: 2 }}>
                    {t('admin:reports.topClients.minThreshold')}
                  </Box>
                  <BreakdownTable
                    headers={[
                      t('admin:reports.topClients.client'),
                      t('admin:reports.topClients.noShows'),
                      t('admin:reports.topClients.totalBooked'),
                      t('admin:reports.topClients.noShowRate'),
                    ]}
                    rows={(report.topClients ?? []).map((row) => ({
                      id: row.clientId,
                      cells: [row.name, row.noShowCount, row.totalBooked, fmt(row.noShowRate)],
                    }))}
                    onRowClick={(clientId) => navigate(`/admin/clients/${clientId}`)}
                  />
                </AppCard>
              </Box>
            )}
          </Box>
        )}

        {hasReporting && activeTab === 'retention' && businessId && (
          <RetentionReportTab businessId={businessId} />
        )}
      </Box>

      {/* Phase 6 Nudge */}
      <AppNudge
        open={nudgeOpen && showExportCsvNudge}
        onClose={() => {
          recordExportCsv();
          setNudgeOpen(false);
        }}
        // eslint-disable-next-line react-hooks/refs
        anchorEl={exportButtonRef.current}
        placement="bottom-start"
        {...exportCsvProps}
        cta={{
          label: exportCsvProps.cta?.label ?? '',
          onClick: () => {
            recordExportCsv();
            setNudgeOpen(false);
            if (report) downloadCsv(report);
          },
        }}
      />
    </AppLayout>
  );
}

// ---------------------------------------------------------------------------
// Retention tab
// ---------------------------------------------------------------------------

const SEGMENT_COLORS: Record<string, string> = {
  vip: colors.warning,
  frequent: colors.accent,
  lapsed: colors.danger,
  newClients: colors.success,
  uncategorized: colors.textMuted,
};

function downloadRetentionCsv(report: GetRetentionReportResponse, fmtPct: (n: number) => string) {
  const lines: string[] = [];
  lines.push('Section,Metric,Value');
  lines.push(`KPIs,New clients,${report.kpis.newClients}`);
  lines.push(`KPIs,Returning clients,${report.kpis.returningClients}`);
  lines.push(`KPIs,Repeat rate,${fmtPct(report.kpis.repeatRate)}`);
  lines.push(`KPIs,Churn rate,${fmtPct(report.kpis.churnRate)}`);
  lines.push(`KPIs,Active clients,${report.kpis.activeClients}`);
  lines.push(`KPIs,Total clients with visit,${report.kpis.totalClientsWithVisit}`);
  lines.push('');
  lines.push('Cohort,Size,M+1 Returned,M+1 %,M+2 Returned,M+2 %,M+3 Returned,M+3 %');
  for (const row of report.cohort) {
    lines.push(
      [
        row.cohortMonth,
        row.cohortSize,
        row.m1Returned ?? '—',
        row.m1Pct != null ? fmtPct(row.m1Pct) : '—',
        row.m2Returned ?? '—',
        row.m2Pct != null ? fmtPct(row.m2Pct) : '—',
        row.m3Returned ?? '—',
        row.m3Pct != null ? fmtPct(row.m3Pct) : '—',
      ].join(',')
    );
  }
  lines.push('');
  lines.push('Segment,Count');
  lines.push(`VIP,${report.segments.vip}`);
  lines.push(`Frequent,${report.segments.frequent}`);
  lines.push(`Lapsed,${report.segments.lapsed}`);
  lines.push(`New,${report.segments.newClients}`);
  lines.push(`Uncategorized,${report.segments.uncategorized}`);

  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `tilly-retention-report-${report.filters.from}-${report.filters.to}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function RetentionReportTab({ businessId }: { businessId: string }) {
  const { t } = useTranslation(['admin']);

  const [filters, setFilters] = useState<Pick<Filters, 'preset' | 'from' | 'to'>>(() => ({
    preset: defaultFilters().preset,
    from: defaultFilters().from,
    to: defaultFilters().to,
  }));

  const {
    data: report,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['retention-report', businessId, filters.from, filters.to],
    queryFn: () =>
      adminReportsApi.getRetentionReport(businessId, {
        from: filters.from,
        to: filters.to,
      }),
    enabled: !!businessId,
  });

  function setPreset(preset: DatePreset) {
    const to = today();
    let from: string;
    switch (preset) {
      case '30d':
        from = daysAgo(30);
        break;
      case '12mo':
        from = daysAgo(365);
        break;
      default:
        from = daysAgo(90);
    }
    setFilters({ preset, from, to });
  }

  const fmtPct = (n: number) => `${(n * 100).toFixed(1)}%`;

  const segmentChartData = useMemo(() => {
    if (!report) return [];
    return [
      { name: t('admin:reports.retention.segments.vip'), value: report.segments.vip, key: 'vip' },
      {
        name: t('admin:reports.retention.segments.frequent'),
        value: report.segments.frequent,
        key: 'frequent',
      },
      {
        name: t('admin:reports.retention.segments.lapsed'),
        value: report.segments.lapsed,
        key: 'lapsed',
      },
      {
        name: t('admin:reports.retention.segments.new'),
        value: report.segments.newClients,
        key: 'newClients',
      },
      {
        name: t('admin:reports.retention.segments.uncategorized'),
        value: report.segments.uncategorized,
        key: 'uncategorized',
      },
    ].filter((s) => s.value > 0);
  }, [report, t]);

  return (
    <Box>
      {/* Sub-header row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ fontSize: 16, fontWeight: 600, color: colors.textPrimary }}>
          {t('admin:reports.retention.title')}
        </Box>
        {report && (
          <button
            onClick={() => downloadRetentionCsv(report, fmtPct)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 14px',
              borderRadius: radius.sm,
              border: `1px solid ${colors.border}`,
              background: colors.surface,
              color: colors.textSecondary,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            <Download size={15} />
            {t('admin:reports.exportCsv')}
          </button>
        )}
      </Box>

      {/* Date preset buttons */}
      <AppCard sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          {(['30d', '90d', '12mo'] as DatePreset[]).map((p) => (
            <button
              key={p}
              onClick={() => setPreset(p)}
              style={{
                padding: '6px 14px',
                borderRadius: radius.sm,
                border: `1px solid ${filters.preset === p ? colors.accent : colors.border}`,
                background: filters.preset === p ? colors.accentLight : colors.surface,
                color: filters.preset === p ? colors.accentHover : colors.textSecondary,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {t(`admin:reports.filters.last${p === '30d' ? '30' : p === '90d' ? '90' : '12mo'}`)}
            </button>
          ))}
        </Box>
      </AppCard>

      {isLoading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <AppSkeleton height={120} />
          <AppSkeleton height={300} />
        </Box>
      )}

      {isError && (
        <Box sx={{ color: colors.error, py: 4, textAlign: 'center' }}>
          {t('admin:reports.error')}
        </Box>
      )}

      {report && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* KPI Cards */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <KpiCard
              label={t('admin:reports.retention.kpis.newClients')}
              value={String(report.kpis.newClients)}
              sub={t('admin:reports.retention.kpis.newClientsSub')}
            />
            <KpiCard
              label={t('admin:reports.retention.kpis.returningClients')}
              value={String(report.kpis.returningClients)}
              sub={t('admin:reports.retention.kpis.returningClientsSub')}
            />
            <KpiCard
              label={t('admin:reports.retention.kpis.repeatRate')}
              value={fmtPct(report.kpis.repeatRate)}
              sub={t('admin:reports.retention.kpis.repeatRateSub')}
            />
            <KpiCard
              label={t('admin:reports.retention.kpis.churnRate')}
              value={fmtPct(report.kpis.churnRate)}
              sub={t('admin:reports.retention.kpis.churnRateSub', {
                days: report.kpis.lapsedThresholdDays,
              })}
            />
          </Box>

          {/* Segment donut + Cohort table side by side on large screens */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            {/* Segment donut */}
            <AppCard sx={{ p: 3 }}>
              <SectionTitle>{t('admin:reports.retention.segments.title')}</SectionTitle>
              {segmentChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={segmentChartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="45%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                    >
                      {segmentChartData.map((entry) => (
                        <Cell
                          key={entry.key}
                          fill={SEGMENT_COLORS[entry.key] ?? colors.textMuted}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      formatter={(value: any, name: any) => [value, name]}
                      contentStyle={{
                        borderRadius: radius.sm,
                        border: `1px solid ${colors.border}`,
                        fontSize: 12,
                      }}
                    />
                    <Legend
                      iconType="circle"
                      iconSize={10}
                      wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ color: colors.textMuted, fontSize: 13, py: 4, textAlign: 'center' }}>
                  {t('admin:reports.retention.segments.noData')}
                </Box>
              )}
            </AppCard>

            {/* Cohort retention table */}
            <AppCard sx={{ p: 3 }}>
              <SectionTitle>{t('admin:reports.retention.cohort.title')}</SectionTitle>
              <Box sx={{ fontSize: 12, color: colors.textMuted, mb: 2 }}>
                {t('admin:reports.retention.cohort.subtitle')}
              </Box>
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      {[
                        t('admin:reports.retention.cohort.cohort'),
                        t('admin:reports.retention.cohort.size'),
                        'M+1',
                        'M+2',
                        'M+3',
                      ].map((h) => (
                        <th
                          key={h}
                          style={{
                            textAlign: 'left',
                            padding: '8px 10px',
                            borderBottom: `1px solid ${colors.border}`,
                            color: colors.textMuted,
                            fontWeight: 600,
                            fontSize: 11,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {report.cohort.length === 0 && (
                      <tr>
                        <td
                          colSpan={5}
                          style={{
                            padding: '24px 10px',
                            color: colors.textMuted,
                            textAlign: 'center',
                          }}
                        >
                          {t('admin:reports.retention.cohort.noData')}
                        </td>
                      </tr>
                    )}
                    {report.cohort.map((row, i) => (
                      <tr
                        key={row.cohortMonth}
                        style={{
                          backgroundColor: i % 2 === 0 ? 'transparent' : colors.surfaceRaised,
                        }}
                      >
                        <td
                          style={{
                            padding: '8px 10px',
                            color: colors.textPrimary,
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {row.cohortMonth}
                        </td>
                        <td style={{ padding: '8px 10px', color: colors.textSecondary }}>
                          {row.cohortSize}
                        </td>
                        {(
                          [
                            [row.m1Returned, row.m1Pct],
                            [row.m2Returned, row.m2Pct],
                            [row.m3Returned, row.m3Pct],
                          ] as [number | null, number | null][]
                        ).map(([ret, pct], j) => (
                          <td
                            key={j}
                            style={{
                              padding: '8px 10px',
                              color: pct != null ? colors.textPrimary : colors.textMuted,
                            }}
                          >
                            {pct != null ? `${fmtPct(pct)} (${ret})` : '—'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </AppCard>
          </Box>
        </Box>
      )}
    </Box>
  );
}
